import { FormEvent, useState } from "react";

type PublicAuthPageProps = {
  onPhoneSubmit: (phone: string) => void;
  onRegisterUser: (payload: RegisterForm) => void;
};

type RegisterForm = {
  names: string;
  lastNames: string;
  phone: string;
  email: string;
  birthDate: string;
  gender: string;
};

const GENDER_OPTIONS = ["Masculino", "Femenino", "Otro"] as const;

const INITIAL_REGISTER_FORM: RegisterForm = {
  names: "",
  lastNames: "",
  phone: "",
  email: "",
  birthDate: "",
  gender: "",
};

export default function PublicAuthPage({
  onPhoneSubmit,
  onRegisterUser,
}: PublicAuthPageProps) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [phone, setPhone] = useState("");
  const [loginError, setLoginError] = useState("");
  const [registerData, setRegisterData] = useState<RegisterForm>(
    INITIAL_REGISTER_FORM,
  );
  const [registerError, setRegisterError] = useState("");

  const handlePhoneLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const sanitized = phone.replace(/\D/g, "");

    if (!/^\d{9}$/.test(sanitized)) {
      setLoginError("El teléfono debe tener 9 digitos numericos.");
      return;
    }

    setLoginError("");
    onPhoneSubmit(sanitized);
  };

  const handleRegister = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const sanitizedPhone = registerData.phone.replace(/\D/g, "");

    if (!/^\d{9}$/.test(sanitizedPhone)) {
      setRegisterError("El teléfono debe tener 9 digitos numericos.");
      return;
    }

    if (!registerData.birthDate) {
      setRegisterError("Ingresa tu fecha de nacimiento.");
      return;
    }

    if (!registerData.gender) {
      setRegisterError("Selecciona tu genero.");
      return;
    }

    setRegisterError("");
    onRegisterUser({
      ...registerData,
      phone: sanitizedPhone,
    });
  };

  return (
    <main className="auth-layout">
      <section className="card auth-card public-auth-card">
        <h1>Bienvenido a Boticlick</h1>
        <p className="subtitle">
          Ingresa con teléfono o registra un nuevo usuario.
        </p>

        <div className="auth-switch">
          <button
            type="button"
            className={`auth-switch-button ${mode === "login" ? "is-active" : ""}`}
            onClick={() => setMode("login")}
          >
            Iniciar sesion
          </button>
          <button
            type="button"
            className={`auth-switch-button ${mode === "register" ? "is-active" : ""}`}
            onClick={() => setMode("register")}
          >
            Registrar usuario
          </button>
        </div>

        {mode === "login" ? (
          <form className="auth-form" onSubmit={handlePhoneLogin}>
            <label htmlFor="public-phone">Teléfono</label>
            <input
              id="public-phone"
              type="text"
              inputMode="numeric"
              maxLength={9}
              placeholder="999999999"
              value={phone}
              onChange={(event) =>
                setPhone(event.target.value.replace(/\D/g, ""))
              }
            />
            {loginError ? <p className="error">{loginError}</p> : null}
            <button type="submit">Continuar</button>
          </form>
        ) : (
          <form
            className="auth-form public-register-form"
            onSubmit={handleRegister}
          >
            <div className="public-register-grid">
              <div className="field-group">
                <label htmlFor="reg-names">Nombres</label>
                <input
                  id="reg-names"
                  type="text"
                  value={registerData.names}
                  onChange={(event) =>
                    setRegisterData((prev) => ({
                      ...prev,
                      names: event.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="field-group">
                <label htmlFor="reg-lastnames">Apellidos</label>
                <input
                  id="reg-lastnames"
                  type="text"
                  value={registerData.lastNames}
                  onChange={(event) =>
                    setRegisterData((prev) => ({
                      ...prev,
                      lastNames: event.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="field-group">
                <label htmlFor="reg-phone">Teléfono</label>
                <input
                  id="reg-phone"
                  type="text"
                  inputMode="numeric"
                  maxLength={9}
                  value={registerData.phone}
                  onChange={(event) =>
                    setRegisterData((prev) => ({
                      ...prev,
                      phone: event.target.value.replace(/\D/g, ""),
                    }))
                  }
                  required
                />
              </div>
              <div className="field-group">
                <label htmlFor="reg-email">Correo</label>
                <input
                  id="reg-email"
                  type="email"
                  value={registerData.email}
                  onChange={(event) =>
                    setRegisterData((prev) => ({
                      ...prev,
                      email: event.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="field-group">
                <label htmlFor="reg-birthdate">Fecha de nacimiento</label>
                <input
                  id="reg-birthdate"
                  type="date"
                  value={registerData.birthDate}
                  onChange={(event) =>
                    setRegisterData((prev) => ({
                      ...prev,
                      birthDate: event.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="field-group">
                <label htmlFor="reg-gender">Genero</label>
                <select
                  id="reg-gender"
                  value={registerData.gender}
                  onChange={(event) =>
                    setRegisterData((prev) => ({
                      ...prev,
                      gender: event.target.value,
                    }))
                  }
                  required
                >
                  <option value="">Selecciona</option>
                  {GENDER_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {registerError ? <p className="error">{registerError}</p> : null}
            <button type="submit">Registrar usuario</button>
          </form>
        )}
      </section>
    </main>
  );
}
